import { useQuery } from "@tanstack/react-query";
import type { MeResource } from "@sarva/api-client";
import { api, meKey, useSessionState } from "@/shared/api";

/**
 * `GET /api/v1/me` — the actor, the tenant, the active practice and the
 * **resolved** permission set.
 *
 * The generated type says why this is a request rather than a token claim:
 * "`permissions` is the *resolved* set … read from the database on every
 * request rather than carried in the token, so revoking a role takes effect on
 * the next call instead of at token expiry."
 *
 * That is also why it is not cached for long. A `staleTime` of a minute means a
 * revoked role disappears from the UI within a minute of the API already
 * refusing it — and the UI is only ergonomics either way (doc 27 §7).
 */
export function useMe() {
  const { credentials, practiceId } = useSessionState();

  return useQuery<MeResource>({
    queryKey: meKey(practiceId),
    queryFn: async () => {
      const envelope = await api.getMe();
      return envelope.data;
    },
    enabled: credentials !== null,
    staleTime: 60_000,
  });
}
